import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import { API_KEY } from '../api';
import bg from '../assets/footer-bg.jpg';
import Button, { OutlineButton } from './Button'; // Import Button and OutlineButton
import PosterGridCard from './PosterGridCard';
import { getWatchlist, toggleWatchlistItem } from '../watchlistStorage';
import './Movies.css';

const Movies = () => {
    const [movies, setMovies] = useState([]);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [query, setQuery] = useState('');
    const [searchParams, setSearchParams] = useSearchParams();
    const [selectedGenres, setSelectedGenres] = useState([]);
    const [genres, setGenres] = useState([]);
    const [selectedYears, setSelectedYears] = useState([]);
    const [years, setYears] = useState([]);
    const [includeAdult, setIncludeAdult] = useState(false);
    const [selectedLanguage, setSelectedLanguage] = useState('');
    const [languages, setLanguages] = useState([]);
    const [bookmarkedIds, setBookmarkedIds] = useState(new Set());

    useEffect(() => {
        const refreshBookmarks = () => {
            setBookmarkedIds(new Set(getWatchlist('movie').map((item) => String(item.id))));
        };

        refreshBookmarks();
        window.addEventListener('ark-play-watchlist-updated', refreshBookmarks);
        window.addEventListener('storage', refreshBookmarks);

        return () => {
            window.removeEventListener('ark-play-watchlist-updated', refreshBookmarks);
            window.removeEventListener('storage', refreshBookmarks);
        };
    }, []);

    useEffect(() => {
        fetchGenres();
        fetchLanguages();
        fetchYears();
        const searchQuery = searchParams.get('search') || '';
        const genreFilter = searchParams.get('genres') || '';
        const yearFilter = searchParams.get('years') || '';
        const adultFilter = searchParams.get('adult') === 'true';
        const languageFilter = searchParams.get('language') || '';
        const pageParam = parseInt(searchParams.get('page')) || 1;
        setQuery(searchQuery);
        setSelectedGenres(genreFilter.split(',').filter(Boolean));
        setSelectedYears(yearFilter.split(',').filter(Boolean));
        setIncludeAdult(adultFilter);
        setSelectedLanguage(languageFilter);
        fetchMovies(pageParam, searchQuery, genreFilter, yearFilter, adultFilter, languageFilter);
    }, [searchParams]);

    const fetchGenres = async () => {
        try {
            const response = await axios.get(`https://api.themoviedb.org/3/genre/movie/list?api_key=${API_KEY}`);
            setGenres(response.data.genres);
        } catch (error) {
            console.error("Error fetching genres:", error);
        }
    };

    const fetchYears = () => {
        const currentYear = new Date().getFullYear();
        const years = Array.from({ length: currentYear - 1980 + 1 }, (_, i) => 1980 + i);
        setYears(years);
    };

    const fetchLanguages = async () => {
        try {
            const response = await axios.get(`https://api.themoviedb.org/3/configuration/languages?api_key=${API_KEY}`);
            setLanguages(response.data.filter((language) => language.iso_639_1 && language.iso_639_1 !== 'ko' && language.iso_639_1 !== 'ja'));
        } catch (error) {
            console.error('Error fetching languages:', error);
        }
    };

    const fetchMovies = async (page, query = '', genreIds = '', yearIds = '', includeAdult = false, language = '') => {
        try {
            const genreFilter = [genreIds, language === 'anime' ? '16' : ''].filter(Boolean).join(',');
            const languageFilter = language ? `&with_original_language=${language === 'anime' ? 'ja' : language}` : '';
            const url = `https://api.themoviedb.org/3/discover/movie?api_key=${API_KEY}&language=en-US&sort_by=popularity.desc&include_adult=${includeAdult}&include_video=false&page=${page}&with_genres=${genreFilter}${query ? `&with_text_query=${encodeURIComponent(query)}` : ''}${yearIds ? `&primary_release_year=${yearIds}` : ''}${languageFilter}`;
            const res = await axios.get(url);
            setMovies(res.data.results);
            setTotalPages(res.data.total_pages);
            setPage(page);
        } catch (error) {
            console.error("Error fetching movies:", error);
        }
    };

    const handleSearch = () => {
        setSearchParams({
            search: query,
            genres: selectedGenres.join(','),
            years: selectedYears.join(','),
            adult: includeAdult.toString(),
            language: selectedLanguage,
            page: 1
        });
    };

    const handleGenreChange = (genreId) => {
        setSelectedGenres(prev =>
            prev.includes(genreId)
                ? prev.filter(id => id !== genreId)
                : [...prev, genreId]
        );
    };

    const handleYearChange = (year) => {
        setSelectedYears(prev =>
            prev.includes(year.toString())
                ? prev.filter(id => id !== year.toString())
                : [...prev, year.toString()]
        );
    };

    const toggleFilters = () => {
        const filtersContainer = document.querySelector('.filters-container');
        filtersContainer.classList.toggle('active');
    };

    const handleLanguageChange = (language) => {
        setSearchParams({
            search: query,
            genres: selectedGenres.join(','),
            years: selectedYears.join(','),
            adult: includeAdult.toString(),
            language,
            page: 1
        });
    };

    const changePage = (newPage) => {
        setSearchParams({
            search: query,
            genres: selectedGenres.join(','),
            years: selectedYears.join(','),
            adult: includeAdult.toString(),
            language: selectedLanguage,
            page: newPage
        });
    };

    const handleBookmarkToggle = (movie) => {
        const added = toggleWatchlistItem('movie', {
            id: movie.id,
            title: movie.title || 'Untitled Movie',
            posterPath: movie.poster_path,
        });

        setBookmarkedIds((currentIds) => {
            const updatedIds = new Set(currentIds);
            if (added) {
                updatedIds.add(String(movie.id));
            } else {
                updatedIds.delete(String(movie.id));
            }
            return updatedIds;
        });
    };

    const renderPagination = () => {
        let pages = [];
        const totalDisplayPages = 10;
        const startPage = Math.max(1, page - 4);
        const endPage = Math.min(totalPages, startPage + totalDisplayPages - 1);

        for (let i = startPage; i <= endPage; i++) {
            pages.push(
                <button
                    key={i}
                    onClick={() => changePage(i)}
                    className={`pagination-button ${i === page ? 'active' : ''}`}
                >
                    {i}
                </button>
            );
        }

        return (
            <div className="pagination-container">
                {startPage > 1 && (
                    <button className="pagination-arrow" onClick={() => changePage(startPage - 1)}>
                        &laquo;
                    </button>
                )}
                {pages}
                {endPage < totalPages && (
                    <button className="pagination-arrow" onClick={() => changePage(endPage + 1)}>
                        &raquo;
                    </button>
                )}
            </div>
        );
    };

    return (
        <div className='movies-page'>
            <div className='background-section' style={{ backgroundImage: `url(${bg})` }}>
                <div className='content-container'>
                    <h2>Movies</h2>
                </div>
            </div>
            <div className="search-bar">
                <input
                    type="text"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                    placeholder="Search for movies..."
                />
                <Button className="small" onClick={handleSearch}>Search</Button>
                <OutlineButton className="small" onClick={toggleFilters}>Filters</OutlineButton>
                <select className="movie-language-select" value={selectedLanguage} onChange={(event) => handleLanguageChange(event.target.value)} aria-label="Filter movies by language">
                    <option value="">All languages</option>
                    <option value="ko">Korean Movies</option>
                    <option value="ja">Japanese Movies</option>
                    <option value="zh">Chinese Movies</option>
                    <option value="anime">Anime Movies</option>
                    <optgroup label="Other languages">
                        {languages.filter((language) => language.iso_639_1 !== 'zh').map((language) => <option key={language.iso_639_1} value={language.iso_639_1}>{language.english_name} ({language.iso_639_1})</option>)}
                    </optgroup>
                </select>
            </div>
            <div className="filters-container">
                <div className="filters-grid">
                    <h3>Genres:</h3>
                    {genres.map(genre => (
                        <label key={genre.id}>
                            <input
                                type="checkbox"
                                value={genre.id}
                                checked={selectedGenres.includes(genre.id.toString())}
                                onChange={() => handleGenreChange(genre.id.toString())}
                            />
                            {genre.name}
                        </label>
                    ))}
                    <h3>Year:</h3>
                    {years.map(year => (
                        <label key={year}>
                            <input
                                type="checkbox"
                                value={year}
                                checked={selectedYears.includes(year.toString())}
                                onChange={() => handleYearChange(year)}
                            />
                            {year}
                        </label>
                    ))}
                    <h3>Adults:</h3>
                    <label>
                        <input
                            type="checkbox"
                            checked={includeAdult}
                            onChange={(e) => setIncludeAdult(e.target.checked)}
                        />
                        Include Adult Content
                    </label>
                </div>
            </div>
            <div className='grid-view'>
                {movies.map(movie => (
                    <PosterGridCard
                        key={movie.id}
                        to={`/movies/${movie.id}`}
                        posterPath={movie.poster_path}
                        title={movie.title}
                        isBookmarked={bookmarkedIds.has(String(movie.id))}
                        onBookmarkToggle={() => handleBookmarkToggle(movie)}
                    />
                ))}
            </div>
            {renderPagination()}
        </div>
    );
};

export default Movies;
